import React, { useMemo } from "react";

/**
 * TechBackdrop
 * Reusable decorative background built from inline SVG. Variants:
 *   - "circuit" : circuit traces with little nodes & solder pads
 *   - "hex"     : hexagonal honeycomb grid
 *   - "rain"    : binary / hex digit rain
 *   - "lines"   : flowing data lines arching across
 *   - "topology": network topology with connecting nodes
 *
 * All variants pull colour from the active theme via CSS variables, so they
 * automatically retint when the theme switches.
 */
export default function TechBackdrop({
  variant = "circuit",
  className = "",
  opacity = 0.5
}) {
  const stroke = "hsl(var(--brand-700))";
  const accent = "hsl(var(--accentc-500))";

  const node = (cx, cy, r = 2.2, fill = stroke) => (
    <circle cx={cx} cy={cy} r={r} fill={fill} />
  );

  const Circuit = (
    <svg
      viewBox="0 0 800 400"
      preserveAspectRatio="xMidYMid slice"
      className="w-full h-full"
      aria-hidden="true"
    >
      <g fill="none" stroke={stroke} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* Outer ring trace */}
        <path d="M40 60 L160 60 L180 80 L300 80 L320 60 L520 60 L540 80 L760 80" />
        <path d="M40 130 L120 130 L140 150 L260 150 L280 130 L420 130 L440 150 L600 150 L620 130 L760 130" />
        <path d="M40 220 L160 220 L180 200 L240 200 L260 220 L380 220 L400 200 L500 200 L520 220 L760 220" />
        <path d="M40 300 L120 300 L140 320 L300 320 L320 300 L460 300 L480 320 L600 320 L620 300 L760 300" />
        {/* Verticals */}
        <path d="M180 80 L180 200" />
        <path d="M520 80 L520 220" />
        <path d="M320 60 L320 300" />
        <path d="M620 130 L620 300" />
        {/* Diagonals */}
        <path d="M260 150 L300 80" />
        <path d="M440 150 L420 130" />
        <path d="M500 200 L540 80" />
      </g>

      {/* Solder pads / nodes */}
      <g fill={stroke}>
        {[
          [40, 60], [180, 80], [320, 60], [540, 80], [760, 80],
          [40, 130], [140, 150], [280, 130], [440, 150], [620, 130], [760, 130],
          [40, 220], [180, 200], [260, 220], [400, 200], [520, 220], [760, 220],
          [40, 300], [140, 320], [320, 300], [480, 320], [620, 300]
        ].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3.2" />
            <circle cx={x} cy={y} r="1.4" fill="white" />
          </g>
        ))}
      </g>

      {/* Animated data pulses along traces */}
      <circle r="3" fill={accent}>
        <animateMotion
          dur="7s"
          repeatCount="indefinite"
          path="M40 60 L160 60 L180 80 L300 80 L320 60 L520 60 L540 80 L760 80"
        />
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="7s" repeatCount="indefinite" />
      </circle>
      <circle r="3" fill={accent}>
        <animateMotion
          dur="9s"
          begin="2s"
          repeatCount="indefinite"
          path="M760 220 L520 220 L500 200 L400 200 L380 220 L260 220 L240 200 L180 200 L160 220 L40 220"
        />
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="9s" begin="2s" repeatCount="indefinite" />
      </circle>
      <circle r="2.5" fill={accent}>
        <animateMotion
          dur="6s"
          begin="3.5s"
          repeatCount="indefinite"
          path="M320 60 L320 300"
        />
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="6s" begin="3.5s" repeatCount="indefinite" />
      </circle>

      {/* IC / chip rectangles */}
      <g fill="none" stroke={stroke} strokeWidth="1.2">
        <rect x="220" y="100" width="80" height="38" rx="3" />
        <rect x="450" y="240" width="80" height="38" rx="3" />
        {[226, 234, 242, 250, 258, 266, 274, 282, 290].map((x) => (
          <line key={x} x1={x} y1="100" x2={x} y2="92" />
        ))}
        {[226, 234, 242, 250, 258, 266, 274, 282, 290].map((x) => (
          <line key={`b${x}`} x1={x} y1="138" x2={x} y2="146" />
        ))}
        {[456, 464, 472, 480, 488, 496, 504, 512, 520].map((x) => (
          <line key={`c${x}`} x1={x} y1="240" x2={x} y2="232" />
        ))}
        {[456, 464, 472, 480, 488, 496, 504, 512, 520].map((x) => (
          <line key={`d${x}`} x1={x} y1="278" x2={x} y2="286" />
        ))}
      </g>
      <text x="240" y="124" fontFamily="monospace" fontSize="9" fill={stroke} opacity=".75">PWR-VM</text>
      <text x="470" y="264" fontFamily="monospace" fontSize="9" fill={stroke} opacity=".75">HACMP</text>
    </svg>
  );

  const Hex = useMemo(() => {
    const cells = [];
    const r = 26;
    const w = r * Math.sqrt(3);
    const h = r * 1.5;
    for (let row = 0; row < 12; row++) {
      for (let col = 0; col < 18; col++) {
        const x = col * w + (row % 2 ? w / 2 : 0);
        const y = row * h;
        const d =
          `M ${x} ${y - r} L ${x + w/2} ${y - r/2} L ${x + w/2} ${y + r/2} ` +
          `L ${x} ${y + r} L ${x - w/2} ${y + r/2} L ${x - w/2} ${y - r/2} Z`;
        cells.push({ d, key: `${row}-${col}` });
      }
    }
    return cells;
  }, []);

  const HexBg = (
    <svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" className="w-full h-full" aria-hidden="true">
      <g fill="none" stroke={stroke} strokeWidth="0.8">
        {Hex.map((c) => (
          <path key={c.key} d={c.d} />
        ))}
      </g>
    </svg>
  );

  const Rain = useMemo(() => {
    const cols = 28;
    const stream = [];
    const chars = "01ABCDEF#$/=:>";
    for (let c = 0; c < cols; c++) {
      const xPct = (c / cols) * 100;
      const dur = 8 + Math.random() * 12;
      const delay = -Math.random() * dur;
      const length = 6 + Math.floor(Math.random() * 9);
      const lines = [];
      for (let i = 0; i < length; i++) {
        lines.push(chars[Math.floor(Math.random() * chars.length)]);
      }
      stream.push({ xPct, dur, delay, lines, key: c });
    }
    return stream;
  }, []);

  const RainBg = (
    <div className="relative w-full h-full overflow-hidden font-mono text-[12px] leading-[16px]">
      {Rain.map((s) => (
        <div
          key={s.key}
          className="absolute top-0 -translate-y-full whitespace-pre"
          style={{
            left: `${s.xPct}%`,
            color: stroke,
            animation: `rain ${s.dur}s linear ${s.delay}s infinite`
          }}
        >
          {s.lines.map((ch, i) => (
            <div key={i} style={{ opacity: 0.15 + (i / s.lines.length) * 0.55 }}>
              {ch}
            </div>
          ))}
        </div>
      ))}
      <style>{`
        @keyframes rain {
          0%   { transform: translateY(-50%); }
          100% { transform: translateY(120vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-rain] { animation: none !important; }
        }
      `}</style>
    </div>
  );

  const Lines = (
    <svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" className="w-full h-full" aria-hidden="true">
      <g fill="none" stroke={stroke} strokeWidth="1">
        <path d="M-20 320 C 200 280, 360 120, 820 200" />
        <path d="M-20 360 C 260 200, 480 240, 820 80" />
        <path d="M-20 240 C 220 320, 520 60, 820 260" />
        <path d="M-20 200 C 320 100, 500 380, 820 140" />
      </g>
      <g fill={accent}>
        <circle r="3">
          <animateMotion dur="10s" repeatCount="indefinite"
            path="M-20 320 C 200 280, 360 120, 820 200" />
        </circle>
        <circle r="3">
          <animateMotion dur="13s" begin="3s" repeatCount="indefinite"
            path="M-20 360 C 260 200, 480 240, 820 80" />
        </circle>
        <circle r="2.5">
          <animateMotion dur="11s" begin="6s" repeatCount="indefinite"
            path="M-20 200 C 320 100, 500 380, 820 140" />
        </circle>
      </g>
    </svg>
  );

  const Topology = (
    <svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" className="w-full h-full" aria-hidden="true">
      {(() => {
        const nodes = [
          [120, 90], [260, 60], [400, 110], [540, 70], [680, 130],
          [180, 220], [340, 250], [500, 230], [640, 280],
          [240, 360], [420, 340], [600, 360]
        ];
        const edges = [
          [0,1],[1,2],[2,3],[3,4],[0,5],[1,5],[2,6],[3,7],[4,8],
          [5,6],[6,7],[7,8],[5,9],[6,9],[6,10],[7,10],[8,11],[10,11]
        ];
        return (
          <g>
            <g stroke={stroke} strokeWidth="0.9" opacity="0.55" fill="none">
              {edges.map(([a, b], i) => (
                <line key={i} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} />
              ))}
            </g>
            <g>
              {nodes.map(([x, y], i) => (
                <g key={i}>
                  <circle cx={x} cy={y} r="6" fill="white" stroke={stroke} strokeWidth="1.5" />
                  <circle cx={x} cy={y} r="2.4" fill={accent}>
                    <animate attributeName="opacity" values="0.4;1;0.4" dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
                  </circle>
                </g>
              ))}
            </g>
            {edges.slice(0, 6).map(([a, b], i) => (
              <circle key={`p${i}`} r="2.5" fill={accent}>
                <animateMotion
                  dur={`${5 + i}s`}
                  begin={`${i * 0.7}s`}
                  repeatCount="indefinite"
                  path={`M ${nodes[a][0]} ${nodes[a][1]} L ${nodes[b][0]} ${nodes[b][1]}`}
                />
              </circle>
            ))}
          </g>
        );
      })()}
    </svg>
  );

  const variants = {
    circuit: Circuit,
    hex: HexBg,
    rain: RainBg,
    lines: Lines,
    topology: Topology
  };

  return (
    <div
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ opacity }}
      aria-hidden="true"
    >
      {variants[variant] || Circuit}
    </div>
  );
}
